import { useEffect, useState } from "react";

/**
 * Public, unauthenticated supervisor sign-off page (ADR-013 §3). Opened from the
 * Approve / Return buttons in the sign-off email: `/signoff/action#t=<token>&d=…`.
 * No MSAL — a sibling of <App />. The token is the credential; the mutation is a
 * POST so link-scanners that pre-open the URL only render this page, they can't
 * action it. One tap confirms.
 *
 * te reo is minimal here (public page, outside the i18n provider) — English with
 * a greeting. Full bilingual is a follow-up once Des reviews.
 */

const API_BASE: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  "http://localhost:7071/api/v1";

type Summary = {
  status: string;
  whanau: string;
  cycleLabel: string | null;
  completedBy: string | null;
  sessionAt: string | null;
};

type State =
  | { kind: "loading" }
  | { kind: "invalid" }
  | { kind: "error"; message: string }
  | { kind: "ready"; summary: Summary }
  | { kind: "submitting" }
  | { kind: "submitted"; decision: "approve" | "return" };

function readHash(key: string): string | null {
  const hash = window.location.hash.replace(/^#/, "");
  const v = new URLSearchParams(hash).get(key);
  return v && v.trim() ? v.trim() : null;
}

export function SignoffActionPage() {
  const token = readHash("t");
  const initialDecision = readHash("d");
  const [state, setState] = useState<State>({ kind: "loading" });
  const [showReturn, setShowReturn] = useState(initialDecision === "return");
  const [note, setNote] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState({ kind: "invalid" });
      return;
    }
    void (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/public/signoff/action?token=${encodeURIComponent(token)}`,
          { headers: { accept: "application/json" } },
        );
        if (cancelled) return;
        if (res.status === 410 || res.status === 400) {
          setState({ kind: "invalid" });
          return;
        }
        if (!res.ok) {
          setState({ kind: "error", message: `Error ${String(res.status)}` });
          return;
        }
        const data = (await res.json()) as Summary;
        setState({ kind: "ready", summary: data });
      } catch {
        if (!cancelled) setState({ kind: "error", message: "Network error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function submit(decision: "approve" | "return") {
    if (!token) return;
    if (decision === "return" && !note.trim()) return;
    setState({ kind: "submitting" });
    try {
      const res = await fetch(`${API_BASE}/public/signoff/action`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          accept: "application/json",
        },
        body: JSON.stringify({
          token,
          decision,
          note: decision === "return" ? note.trim() : null,
        }),
      });
      if (res.ok) {
        setState({ kind: "submitted", decision });
        return;
      }
      if (res.status === 409) {
        setState({
          kind: "error",
          message: "This assessment has already been actioned.",
        });
        return;
      }
      if (res.status === 410) {
        setState({ kind: "invalid" });
        return;
      }
      setState({ kind: "error", message: `Error ${String(res.status)}` });
    } catch {
      setState({ kind: "error", message: "Network error — please try again." });
    }
  }

  return (
    <main
      style={{
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        maxWidth: "560px",
        margin: "0 auto",
        padding: "32px 20px",
        color: "#1a2547",
      }}
    >
      <p style={{ fontSize: "11px", letterSpacing: "1px", margin: 0 }}>
        TE TOHU O TE ORA O NGĀTI AWA
      </p>
      <p
        style={{
          fontSize: "10px",
          letterSpacing: "0.5px",
          color: "#0d7377",
          margin: "2px 0 20px",
        }}
      >
        Whānau Factor Profile · Assessment sign-off
      </p>

      {state.kind === "loading" && <p>Loading…</p>}

      {state.kind === "invalid" && (
        <p>
          This sign-off link is invalid or has expired. If you still need to
          review the assessment, open it in the Whānau Factor app.
        </p>
      )}

      {state.kind === "error" && (
        <p style={{ color: "#c0392b" }}>{state.message}</p>
      )}

      {state.kind === "submitted" && (
        <div>
          <h1 style={{ fontSize: "22px" }}>
            {state.decision === "approve" ? "Approved ✓" : "Returned ✓"}
          </h1>
          <p style={{ color: "#5a6578" }}>
            {state.decision === "approve"
              ? "Ngā mihi — the assessment is approved. You can close this page."
              : "The assessment has been returned to the kaimahi with your note. You can close this page."}
          </p>
        </div>
      )}

      {(state.kind === "ready" || state.kind === "submitting") && (
        <RenderReady
          summary={state.kind === "ready" ? state.summary : null}
          submitting={state.kind === "submitting"}
          showReturn={showReturn}
          note={note}
          onNote={setNote}
          onShowReturn={() => setShowReturn(true)}
          onApprove={() => void submit("approve")}
          onReturn={() => void submit("return")}
        />
      )}
    </main>
  );
}

function RenderReady({
  summary,
  submitting,
  showReturn,
  note,
  onNote,
  onShowReturn,
  onApprove,
  onReturn,
}: {
  summary: Summary | null;
  submitting: boolean;
  showReturn: boolean;
  note: string;
  onNote: (s: string) => void;
  onShowReturn: () => void;
  onApprove: () => void;
  onReturn: () => void;
}) {
  const btn = {
    padding: "12px 22px",
    borderRadius: "8px",
    border: "none",
    color: "#fff",
    fontSize: "1rem",
    fontWeight: 600,
    cursor: "pointer",
  } as const;

  return (
    <div>
      {summary && (
        <>
          <h1 style={{ fontSize: "22px", margin: "0 0 4px" }}>
            {summary.whanau}
          </h1>
          <p style={{ color: "#5a6578", margin: "0 0 4px" }}>
            {summary.cycleLabel
              ? `${summary.cycleLabel} assessment`
              : "Assessment"}
            {summary.sessionAt
              ? ` · ${new Date(summary.sessionAt).toLocaleDateString("en-NZ")}`
              : ""}
          </p>
          {summary.completedBy && (
            <p style={{ color: "#5a6578", margin: "0 0 16px" }}>
              Completed by: {summary.completedBy}
            </p>
          )}
        </>
      )}

      {summary && summary.status !== "pending" ? (
        <p>
          This assessment has already been {summary.status}. No further action
          is needed.
        </p>
      ) : (
        <>
          <p style={{ color: "#5a6578", fontSize: "0.9rem" }}>
            The full assessment is in the email that brought you here. Confirm
            your decision below.
          </p>
          {!showReturn ? (
            <div
              style={{
                display: "flex",
                gap: "10px",
                flexWrap: "wrap",
                marginTop: "8px",
              }}
            >
              <button
                type="button"
                style={{ ...btn, background: "#2d7a45" }}
                disabled={submitting}
                onClick={onApprove}
              >
                {submitting ? "Saving…" : "Approve"}
              </button>
              <button
                type="button"
                style={{ ...btn, background: "#c0392b" }}
                disabled={submitting}
                onClick={onShowReturn}
              >
                Return with note
              </button>
            </div>
          ) : (
            <div style={{ marginTop: "8px" }}>
              <label
                htmlFor="signoff-note"
                style={{
                  display: "block",
                  fontWeight: 600,
                  marginBottom: "4px",
                }}
              >
                Note for the kaimahi (required)
              </label>
              <textarea
                id="signoff-note"
                value={note}
                onChange={(e) => onNote(e.target.value)}
                rows={4}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #b9c2cc",
                  font: "inherit",
                }}
                placeholder="What needs another look?"
              />
              <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                <button
                  type="button"
                  style={{
                    ...btn,
                    background: "#c0392b",
                    opacity: note.trim() ? 1 : 0.6,
                  }}
                  disabled={submitting || !note.trim()}
                  onClick={onReturn}
                >
                  {submitting ? "Saving…" : "Confirm return"}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default SignoffActionPage;
